import Lottie from "lottie-react";
import { type LottieRefCurrentProps } from "lottie-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { SplashScreen } from "@capacitor/splash-screen";

import { REBRAND_PRIMARY_COLOR } from "@src/appV2/lib/Theme/rebrandConstants";

import superNurseAnimation from "./superNurseAnimation.json";

const MINIMUM_DISPLAY_MS = 2500;
const FADE_OUT_MS = 400;
const __APP_READY___POLL_MS = 200;

interface MobileSplashOverlayProps {
  onFinished: () => void;
}

export function MobileSplashOverlay({ onFinished }: MobileSplashOverlayProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const dismissedRef = useRef(false);
  const minimumTimeElapsedRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const tryDismiss = useCallback(() => {
    if (window.__APP_READY__ && minimumTimeElapsedRef.current && !dismissedRef.current) {
      dismissedRef.current = true;
      setIsFadingOut(true);
      setTimeout(() => onFinishedRef.current(), FADE_OUT_MS);
    }
  }, []);

  // After minimum display time, start polling for app-ready
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | undefined;

    const minTimer = setTimeout(() => {
      minimumTimeElapsedRef.current = true;

      // App might already be ready
      if (window.__APP_READY__) {
        tryDismiss();
        return;
      }

      // Poll until app is ready
      pollInterval = setInterval(() => {
        if (window.__APP_READY__) {
          clearInterval(pollInterval);
          tryDismiss();
        }
      }, __APP_READY___POLL_MS);
    }, MINIMUM_DISPLAY_MS);

    return () => {
      clearTimeout(minTimer);
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [tryDismiss]);

  const handleLottieReady = useCallback(() => {
    setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 0 }).catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.warn("Failed to hide splash screen:", error);
      });
      lottieRef.current?.play();
    }, 0);
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        backgroundColor: REBRAND_PRIMARY_COLOR,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: isFadingOut ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
      }}
    >
      <div style={{ width: "75vw", transform: "translateY(3px)" }}>
        <Lottie
          loop
          lottieRef={lottieRef}
          autoplay={false}
          animationData={superNurseAnimation}
          onDOMLoaded={handleLottieReady}
        />
      </div>
    </div>
  );
}
